import React from "react";
import PropTypes from "prop-types";
import { Drawer, List } from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import MapIcon from "@mui/icons-material/Map";
import ComputerIcon from "@mui/icons-material/Computer";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import Diversity2Icon from "@mui/icons-material/Diversity2";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { useAuth } from "hooks/useProvideAuth";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import SensorOccupiedOutlinedIcon from "@mui/icons-material/SensorOccupiedOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DataUploadIcon from "../../assets/svg/database_upload_24dp_E8EAED_FILL0_wght400_GRAD0_opsz24.svg";
import { SidebarWrapper, SidebarLinks, SidebarLink, DrawerWrapper, SidebarLinkOptions } from "./Sidebar.style";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ListItemButton from "@mui/material/ListItemButton";
import { BoldMenuText } from "components/common/text/text.component";
import Collapse from "@mui/material/Collapse";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import style from "./sidebar.module.css";

const MenuItems = () => {
  const { isAdmin, isPowerUser, isReadOnly, isDemoUser }: any = useAuth();

  const [open, setOpen] = React.useState<boolean>(false);

  const handleClick = () => {
    setOpen(!open);
  };

  if (isPowerUser) {
    return (
      <>
        <SidebarLink to="/orgs">
          <Diversity2Icon /> Organizations
        </SidebarLink>
        <SidebarLink to="/admins">
          <SupervisorAccountIcon /> Administrators
        </SidebarLink>
        <SidebarLink to="/power-users">
          <PeopleAltIcon /> Power Users
        </SidebarLink>
        <SidebarLink to="/support">
          <SupportAgentIcon /> Support
        </SidebarLink>
        <SidebarLink to="/inventory">
          <img src={DataUploadIcon} alt="" />
          Inventory
        </SidebarLink>
        <ListItemButton sx={{ gap: "16px" }} onClick={handleClick}>
          <SystemUpdateAltOutlinedIcon
            sx={{
              color: open ? "white" : "#CCD1D9",
            }}
          />

          <BoldMenuText
            sx={{
              color: open ? "white" : "#CCD1D9",
            }}
          >
            Software
          </BoldMenuText>
          {open ? (
            <ExpandLess
              sx={{
                color: open ? "white" : "#CCD1D9",
              }}
            />
          ) : (
            <ExpandMore
              sx={{
                color: open ? "white" : "#CCD1D9",
              }}
            />
          )}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <SidebarLinkOptions to="/software-auto">
              <div className={style.menuItemContainer}>
                <UpdateOutlinedIcon sx={{ marginRight: "8px" }} />
                Auto Update
              </div>
            </SidebarLinkOptions>

            <SidebarLinkOptions to="/software-manual">
              <div className={style.menuItemContainer}>
                <BuildCircleOutlinedIcon sx={{ marginRight: "8px" }} />
                Manual Update
              </div>
            </SidebarLinkOptions>
            <SidebarLinkOptions to="/versions">
              <div className={style.menuItemContainer}>
                <CloudDownloadOutlinedIcon sx={{ marginRight: "8px" }} />
                Versions
              </div>
            </SidebarLinkOptions>
            <SidebarLinkOptions to="/manage-versions">
              <div className={style.menuItemContainer}>
                <TuneOutlinedIcon sx={{ marginRight: "8px" }} />
                Manage Versions
              </div>
            </SidebarLinkOptions>
          </List>
        </Collapse>
        <SidebarLink to="/demo-users">
          <SensorOccupiedOutlinedIcon /> Demo Users
        </SidebarLink>
        <SidebarLink to="/device-lookup">
          <SearchOutlinedIcon /> Device Lookup
        </SidebarLink>
      </>
    );
  }

  if (isAdmin) {
    return (
      <>
        <SidebarLink to="/users">
          <PeopleOutlineIcon /> Users
        </SidebarLink>
        <SidebarLink to="/devices">
          <ComputerIcon /> Devices
        </SidebarLink>
        <SidebarLink to="/pools">
          <DevicesOtherIcon /> Pools
        </SidebarLink>
        <SidebarLink to="/map">
          <MapIcon /> Map
        </SidebarLink>
      </>
    );
  }

  if (isDemoUser) {
    return (
      <>
        <SidebarLink to="/devices">
          <ComputerIcon /> Devices
        </SidebarLink>
        <SidebarLink to="/map">
          <MapIcon /> Map
        </SidebarLink>
      </>
    );
  }

  if (isReadOnly) {
    return (
      <>
        <SidebarLink to="/devices">
          <ComputerIcon /> Devices
        </SidebarLink>
      </>
    );
  }

  return (
    <>
      <SidebarLink to="/devices">
        <ComputerIcon /> Devices
      </SidebarLink>
      <SidebarLink to="/pools">
        <DevicesOtherIcon /> Pools
      </SidebarLink>
      <SidebarLink to="/map">
        <MapIcon /> Map
      </SidebarLink>
    </>
  );
};

const Content = ({ isAuthenticated }: any) => (
  <SidebarLinks>
    <div className="awcone__navbar-links_container">{isAuthenticated && <MenuItems />}</div>
  </SidebarLinks>
);
Content.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
};

const Sidebar = ({ showDrawer, onCloseDrawer }: any) => {
  const { isAuthenticated }: any = useAuth();

  return (
    <>
      <SidebarWrapper>
        <Content isAuthenticated={isAuthenticated} />
      </SidebarWrapper>
      <Drawer anchor="left" open={showDrawer} onClose={() => onCloseDrawer?.()}>
        <DrawerWrapper>
          <Content isAuthenticated={isAuthenticated} />
        </DrawerWrapper>
      </Drawer>
    </>
  );
};
Sidebar.propTypes = {
  showDrawer: PropTypes.bool.isRequired,
  onCloseDrawer: PropTypes.func.isRequired,
};

export default Sidebar;
